import { Inject, Injectable } from '@angular/core';
import { OdataOtherTasksInterface } from './odata-other-tasks.interface';
import { Observable, throwError } from 'rxjs';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { catchError, map } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { OdataResponse } from '../../interfaces/online-only.interface';
import { NotificationToGetOtherTasks, NotificationToModifyOtherTask } from '../../models/low-level/notification.model';
import {
    ODataOtherTaskNotificationModel,
    OtherTaskNotificationModel,
} from '../../models/low-level/other-task-notification.model';
import { isDefined } from '../../utilities/is-defined';
import { OnlineOnlyService } from '../online-only/online-only.service';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';

@Injectable({
    providedIn: 'root',
})
export class OdataOtherTasksService implements OdataOtherTasksInterface {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly onlineOnlyService: OnlineOnlyService,
    ) {}

    public getNotificationsByEquipment(
        notificationData: NotificationToGetOtherTasks,
        equipment: string,
    ): Observable<OtherTaskNotificationModel[]> {
        let query = ENDPOINTS.NotificationOtherTasks.toString();

        const filterNotifNo = `DbNotifNo eq '${notificationData.DbNotifNo}'`;
        const filterTaskNo = `DbTaskNo eq '${notificationData.DbTaskNo}'`;
        const filterNotifStatus = `DbNotifStatus eq '${notificationData.DbNotifStatus}'`;
        const filterNotifStartDate = `DbNotifStartDate eq '${notificationData.DbNotifStartDate}'`;
        const filterNotifStartTime = `DbNotifStartTime eq time'${notificationData.DbNotifStartTime}'`;
        const filterNotifEndDate = `DbNotifEndDate eq '${notificationData.DbNotifEndDate}'`;
        const filterNotifEndTime = `DbNotifEndTime eq time'${notificationData.DbNotifEndTime}'`;
        const filterEquipment = `Equipment eq '${equipment}'`;

        query = this.uriUtilities.appendFilterValue(query, filterNotifNo);
        query = this.uriUtilities.appendFilterValue(query, filterTaskNo);
        query = this.uriUtilities.appendFilterValue(query, filterNotifStatus);
        query = this.uriUtilities.appendFilterValue(query, filterNotifStartDate);
        query = this.uriUtilities.appendFilterValue(query, filterNotifStartTime);
        query = this.uriUtilities.appendFilterValue(query, filterNotifEndDate);
        query = this.uriUtilities.appendFilterValue(query, filterNotifEndTime);
        query = this.uriUtilities.appendFilterValue(query, filterEquipment);

        return this.onlineOnlyService.get<ODataOtherTaskNotificationModel[]>(query).pipe(
            map((response: ODataOtherTaskNotificationModel[]) =>
                response.map((notification) => new OtherTaskNotificationModel(notification)).filter(isDefined),
            ),
            catchError((err) => {
                this.log.error(
                    'OdataOtherTasksService: getNotificationsByEquipment - request error: ' + JSON.stringify(err),
                );
                return throwError(() => err);
            }),
        );
    }

    public getNotificationsByCustomer(
        notificationData: NotificationToGetOtherTasks,
        customer: string,
    ): Observable<OtherTaskNotificationModel[]> {
        let query = ENDPOINTS.NotificationOtherTasks.toString();

        const filterNotifNo = `DbNotifNo eq '${notificationData.DbNotifNo}'`;
        const filterTaskNo = `DbTaskNo eq '${notificationData.DbTaskNo}'`;
        const filterNotifStatus = `DbNotifStatus eq '${notificationData.DbNotifStatus}'`;
        const filterNotifStartDate = `DbNotifStartDate eq '${notificationData.DbNotifStartDate}'`;
        const filterNotifStartTime = `DbNotifStartTime eq time'${notificationData.DbNotifStartTime}'`;
        const filterNotifEndDate = `DbNotifEndDate eq '${notificationData.DbNotifEndDate}'`;
        const filterNotifEndTime = `DbNotifEndTime eq time'${notificationData.DbNotifEndTime}'`;
        const filterCustomer = `Customer eq '${customer}'`;

        query = this.uriUtilities.appendFilterValue(query, filterNotifNo);
        query = this.uriUtilities.appendFilterValue(query, filterTaskNo);
        query = this.uriUtilities.appendFilterValue(query, filterNotifStatus);
        query = this.uriUtilities.appendFilterValue(query, filterNotifStartDate);
        query = this.uriUtilities.appendFilterValue(query, filterNotifStartTime);
        query = this.uriUtilities.appendFilterValue(query, filterNotifEndDate);
        query = this.uriUtilities.appendFilterValue(query, filterNotifEndTime);
        query = this.uriUtilities.appendFilterValue(query, filterCustomer);

        return this.onlineOnlyService.get<ODataOtherTaskNotificationModel[]>(query).pipe(
            map((response: ODataOtherTaskNotificationModel[]) =>
                response.map((notification) => new OtherTaskNotificationModel(notification)).filter(isDefined),
            ),
            catchError((err) => {
                this.log.error(
                    'OdataOtherTasksService: getNotificationsByCustomer - request error: ' + JSON.stringify(err),
                );
                return throwError(() => err);
            }),
        );
    }

    public getTask(notificationData: NotificationToModifyOtherTask): Observable<NotificationToModifyOtherTask> {
        const url = ENDPOINTS.NotificationOtherTasks;
        return this.onlineOnlyService.post<OdataResponse<ODataOtherTaskNotificationModel>>(url, notificationData).pipe(
            // Intentionally not returning the response from GW since it takes time to update.
            // Instead, we return the original notification data as-is.
            map(() => notificationData),
            catchError((err) => {
                this.log.error('OdataOtherTasksService: getTask - post request error: ' + JSON.stringify(err));
                return throwError(() => err);
            }),
        );
    }
}
